import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { WhatsAppUnreadProvider } from 'contexts/WhatsAppUnreadContext';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

function DashboardLayoutContent() {
  const { pathname, search } = useLocation();
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const { miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  /** Com shell a `100svh`, impede scroll do documento — o scroll vertical fica no `<main>`; páginas podem ainda ter overflow interno (ex.: chat). */
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  if (menuMasterLoading) return <Loader />;

  const isA4Playground = pathname.includes('/ai-docs/a4-playground/');

  // Header agora só é renderizado no mobile (downLG) — no desktop a navbar foi removida
  // e os itens de notificação/timer foram movidos para a sidebar.
  const showHeader = downLG;

  // Sempre que o header móvel (downLG) aparece, reserva o espaço dele — inclusive
  // na raiz de transcrições, que antes era excluída e fazia as abas ficarem por
  // baixo do header em telas < lg (e no app desktop em janelas estreitas).
  const showToolbarSpacer = showHeader;

  /** Chat em tela cheia: main colado às bordas (evita “caixa” com padding). */
  const mainFlushed =
    (/\/ai-chat\/?$/.test(pathname) && Boolean(new URLSearchParams(search).get('assistantId'))) ||
    /\/whatsapp\/inbox\/?$/.test(pathname) ||
    /\/ai-video\/?$/.test(pathname);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        /* Altura fixa ao viewport: evita que a shell cresça e faça rolar o documento/body */
        height: '100svh',
        maxHeight: '100svh',
        minHeight: 0,
        overflow: 'hidden'
      }}
      data-page={isA4Playground ? 'a4-playground' : 'other'}
    >
      {showHeader && <Header />}
      {!isHorizontal ? <Drawer /> : <HorizontalBar />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          /* minHeight: 0 — com min-height:auto o flex item não encolhe e o documento passa a rolar */
          minHeight: 0,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          /** Chat com `?assistantId=`: rolamento só dentro do SimpleChatPanel, não neste `<main>`. */
          overflowY: mainFlushed ? 'hidden' : 'auto',
          /* Conteúdo em tela cheia: sem “card” com margens ao redor do outlet */
          p: mainFlushed ? 0 : 2,
          pt: mainFlushed ? 0 : 2,
          mt: 0,
          width: '100%'
        }}
      >
        {showToolbarSpacer && (
          <Toolbar sx={{ mt: isHorizontal ? 8 : 0, minHeight: '64px !important' }} />
        )}
        <Container
          maxWidth={false}
          sx={{
            px: '0 !important',
            maxWidth: '100% !important',
            width: '100%',
            minHeight: mainFlushed ? 0 : 'min-content',
            /* Cresce com o outlet para o `<main>` acima aplicar rolagem vertical */
            flex: '1 1 auto',
            gap: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: mainFlushed ? 'hidden' : 'visible'
          }}
        >
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}

export default function DashboardLayout() {
  return (
    <AuthGuard>
      <WhatsAppUnreadProvider>
        <DashboardLayoutContent />
      </WhatsAppUnreadProvider>
    </AuthGuard>
  );
}
